import React, { useState, useEffect, useContext, useCallback } from 'react';
import SafetyBasicInfo from './SafetyBasicInfo';
import SafetyPreparation from './SafetyPreparation';
import SafetyConfig from './SafetyConfig';
import SafetyHazardCategory from './SafetyHazardCategory';
import SafetyProcess from './SafetyProcess';
import SafetyMasterHazardMap from './SafetyMasterHazardMap';
import { AuthContext } from '../../../context/AuthContext';
import { getSafetyBasicInfo } from '../../../api/safetyAssessApi';
import { getManagers } from '../../../api/managerApi';
import '../Safety.css';

function SafetyMaster() {
  const [activeTab, setActiveTab] = useState('basic');
  const { token } = useContext(AuthContext);
  const [useProcess, setUseProcess] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedComCode, setSelectedComCode] = useState('');

  useEffect(() => {
    getManagers(token)
      .then(data => setCompanies(data))
      .catch(err => console.error('회사 목록 조회 실패:', err));
  }, [token]);

  const handleComCodeChange = (e) => {
    setSelectedComCode(e.target.value);
    setActiveTab('basic');
  };

  const loadBasicInfo = useCallback(async () => {
    if (!token || !selectedComCode) return;
    try {
      const data = await getSafetyBasicInfo(token, selectedComCode);
      if (data && data.useProcess !== undefined) {
        setUseProcess(data.useProcess);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token, selectedComCode]);

  useEffect(() => {
    loadBasicInfo();
  }, [loadBasicInfo]);

  const tabs = [
    { id: 'basic', label: '기본 정보', icon: 'bi bi-building', component: <SafetyBasicInfo comCode={selectedComCode} onSaved={loadBasicInfo} /> },
    { id: 'preparation', label: '사전준비', icon: 'bi bi-shield-check', component: <SafetyPreparation comCode={selectedComCode} /> },
    { id: 'config', label: '평가 기준표', icon: 'bi bi-grid-3x3-gap', component: <SafetyConfig comCode={selectedComCode} /> },
    { id: 'category', label: '분류/원인 관리', icon: 'bi bi-list-check', component: <SafetyHazardCategory comCode={selectedComCode} /> },
    { id: 'process', label: '공정 마스터', icon: 'bi bi-gear-wide-connected', component: <SafetyProcess comCode={selectedComCode} /> },
    { id: 'matrix', label: '표준 위험요인 맵핑', icon: 'bi bi-grid-3x3-gap-fill', component: <SafetyMasterHazardMap comCode={selectedComCode} /> }
  ];

  // 공정 미사용일 때 공정 마스터와 표준 위험요인 맵핑 탭 숨김
  const filteredTabs = useProcess
    ? tabs
    : tabs.filter(t => t.id !== 'process' && t.id !== 'matrix');

  const activeTabObj = filteredTabs.find(t => t.id === activeTab) || filteredTabs[0];

  return (
    <div className="sf-page sf-animate" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
      {/* 타이틀 헤더 (카드 밖으로 분리) */}
      <div className="sf-header" style={{ marginBottom: 24, paddingBottom: 14, borderBottom: '1px solid #cbd5e1' }}>
        <div>
          <h3 className="sf-header-title">
            <span className="sf-icon" style={{ background: 'var(--sf-gradient-accent)' }}><i className="bi bi-sliders"></i></span>
            위험성평가 설정관리
          </h3>
          <div className="mb-2 mt-2" style={{ maxWidth: '240px' }}>
            <select
              className="form-select"
              style={{ minWidth: '180px', maxWidth: '240px' }}
              value={selectedComCode}
              onChange={handleComCodeChange}
            >
              <option value="">회사를 선택해주세요.</option>
              {companies.map((c) => (
                <option key={c.M_ComCode} value={c.M_ComCode}>{c.M_ComName}</option>
              ))}
            </select>
          </div>
          <p className="sf-header-desc">
            위험성평가 운영에 필요한 사업장 기본 정보, 평가 기준 매트릭스, 유해위험 요인 및 공정 마스터를 설정합니다.
          </p>
        </div>
      </div>

      {/* 메인 콘텐츠 카드 (테두리 및 그림자를 여기에 적용) */}
      <div className="sf-card" style={{
        background: '#ffffff',
        border: '1px solid #cbd5e1',
        borderRadius: '16px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
        padding: '24px'
      }}>
        {/* ProjectWorkspace 동일 탭 인터페이스 */}
        <div className="d-flex align-items-end" style={{
          borderBottom: '1px solid #dee2e6',
          marginBottom: 24
        }}>
          {filteredTabs.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <div
                key={tab.id}
                className={`px-4 py-2 d-flex align-items-center ${isActive ? 'fw-bold text-primary' : 'fw-semibold text-dark'}`}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isActive ? '#ffffff' : '#e9ecef',
                  borderTop: isActive ? '3px solid #0d6efd' : '1px solid #dee2e6',
                  borderLeft: '1px solid #dee2e6',
                  borderRight: '1px solid #dee2e6',
                  borderBottom: isActive ? '1px solid #ffffff' : '1px solid #dee2e6',
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                  marginBottom: '-1px',
                  fontSize: '0.9rem',
                  minWidth: '120px',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#d3d9df'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`${tab.icon} me-2`} style={{ fontSize: '1.05rem' }}></i>
                {tab.label}
              </div>
            );
          })}
        </div>

        {/* 탭 콘텐츠 영역 */}
        <div key={selectedComCode} className="tab-content-wrapper" style={{ animation: 'fadeIn 0.25s ease-in-out' }}>
          {selectedComCode ? activeTabObj.component : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <i className="bi bi-building" style={{ fontSize: '2.5rem', marginBottom: '12px' }}></i>
              <p style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>조회할 회사를 선택해주세요.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SafetyMaster;
